import { Box } from '@startuptools/ui/Box';
import { Stack } from '@startuptools/ui/Stack';
import { Dialog, DialogActions } from '@startuptools/ui/Dialog';
import { Button } from '@startuptools/ui/Button';
import { Typography } from '@startuptools/ui/Typography';
import * as React from 'react';
import { Trans, useLingui } from '@lingui/react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useFoldersSuspenseQuery } from './file-view.graphql';
import { GqlDocSystemTagKind } from '../../../graphql/base-types.graphql';
import { List } from '@startuptools/ui/joy/List';
import { ListItem } from '@startuptools/ui/joy/ListItem';
import { ListItemContent } from '@startuptools/ui/joy/ListItemContent';
import { ListItemButton } from '@startuptools/ui/joy/ListItemButton';

interface MoveFileDialogProps {
  onClose: () => void;
  moveFile: (fileId: string, folderId: string | null) => Promise<unknown>;
  loading: boolean;
  fileId: string;
  currentFolderId: string | null;
}

const Folders = ({
  onSelect,
  selected,
  currentFolderId,
}: {
  onSelect: (folderId: string | null) => void;
  selected: string | null;
  currentFolderId: string | null;
}) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const { data } = useFoldersSuspenseQuery({
    variables: {
      companyId,
      fileFilter: {
        includeIndirectChildren: true,
        kinds: [GqlDocSystemTagKind.Folder],
      },
    },
  });

  return (
    <List
      variant="outlined"
      sx={{
        maxHeight: '400px',
        overflowY: 'auto',
        borderRadius: 'md',
      }}
    >
      <ListItem>
        <ListItemButton selected={!selected} onClick={() => onSelect(null)}>
          <ListItemContent>
            <Typography level="body-sm">
              <Trans id="Root" />
            </Typography>
          </ListItemContent>
        </ListItemButton>
      </ListItem>
      {data.file.children.models
        .filter(folder => folder.id !== currentFolderId)
        .map(folder => (
          <ListItem key={folder.id}>
            <ListItemButton selected={selected === folder.id} onClick={() => onSelect(folder.id)}>
              <ListItemContent>
                <Typography level="body-sm">
                  {i18n.locale === 'sv' ? folder.filename : folder.filenameSecondaryLang}
                </Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>
        ))}
    </List>
  );
};

export const MoveFileDialog = ({ onClose, fileId, moveFile, loading, currentFolderId }: MoveFileDialogProps) => {
  const [selectedFolderId, setSelectedFolderId] = React.useState<string | null>(null);

  return (
    <Dialog open onClose={onClose}>
      <Stack spacing={2} p={2}>
        <Typography level="h4">
          <Trans id="Move File" />
        </Typography>

        <Box sx={{ minHeight: 400, width: 400 }}>
          <React.Suspense>
            <Folders onSelect={setSelectedFolderId} selected={selectedFolderId} currentFolderId={currentFolderId} />
          </React.Suspense>
        </Box>

        <DialogActions>
          <Button onClick={onClose} variant="plain">
            <Trans id="Cancel" />
          </Button>
          <Button
            loading={loading}
            onClick={async () => {
              await moveFile(fileId, selectedFolderId);
              onClose();
            }}
            disabled={!selectedFolderId}
          >
            <Trans id="Move" />
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};
